import { memo, useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormBuilderCardWrapper } from '../../../FormBuilderCardWrapper';
import { FormBuilderElement } from '../../../FormBuilderElement';
import {
  useFormBuilderFields,
  useFormBuilderFieldWithError,
} from '../../../hooks';
import { EFormBuilderFieldType, TFormBuilderSingleChoiceField } from '@/types';

type TProps = {
  field: TFormBuilderSingleChoiceField;
  index: number;
};

export const FormBuilderSingleChoice =
  /**
   * Single choice field component for form builder with radio buttons, editable label and options
   * @param {TProps} props - Component props
   * @param {TFormBuilderSingleChoiceField} props.field - Single choice field data
   * @param {number} props.index - Field index in the form
   * @returns {JSX.Element} Form builder single choice field component
   */
  memo(({ field, index }: TProps) => {
    const { t } = useTranslation();
    const labelId = useId();

    const [active, setActive] = useState(false);
    const onActiveChange = useCallback(
      (value: boolean) => setActive(value),
      [],
    );

    const { changeFields, removeField, copyField, moveDownField, moveUpField } =
      useFormBuilderFields(field);

    const error =
      useFormBuilderFieldWithError<TFormBuilderSingleChoiceField>(index);

    const optionsWithError = field.options.map((option, index) => ({
      ...option,
      error: !!error?.options?.[index],
    }));
    return (
      <FormBuilderCardWrapper
        variant={EFormBuilderFieldType.SINGLE_CHOICE}
        active={active}
        onActiveChange={onActiveChange}
        onRemove={removeField}
        onDownMove={moveDownField}
        onUpMove={moveUpField}
        onDuplicate={copyField}
        error={!!error}
      >
        <FormBuilderElement
          alert={t('form-builder.single-choice-alert')}
          labelId={labelId}
          label={
            <FormBuilderElement.Label
              label={field.label}
              id={labelId}
              onLabelChange={(label) => changeFields({ label })}
              placeholder={t('form-builder.your-label-here')}
              required={field.required}
              error={!!error?.label}
            />
          }
          required={field.required}
          onRequiredChange={(required) => changeFields({ required })}
          options={
            <FormBuilderElement.Options
              options={optionsWithError}
              onChange={(options) => changeFields({ options })}
              variant="radio"
            />
          }
        />
      </FormBuilderCardWrapper>
    );
  });

FormBuilderSingleChoice.displayName = 'FormBuilderSingleChoice';
